/**
 * LibraryTileCard — one library on the Libraries page. Demo page: /ui-elements/cards and /file-manager (a card per
 * folder: icon tile, title and chevron on one line, description, a footer of facts). The whole card opens the library through the title link
 * (stretched over the card); the review shortcut sits above it.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, type LucideIcon } from 'lucide-react';
import { Badge, Chip } from '../../../components/ui/index.js';
import { Card } from '../../../components/tailgrids/core/card.js';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import {
  reviewActionLabel,
  reviewDestination,
  type LibraryTileLike,
  type LibraryTileState,
} from '../libraryReviewHelpers.js';

export interface LibraryTile extends LibraryTileLike {
  description: string;
  path: string;
  icon: LucideIcon;
  meta: string;
}

interface LibraryTileCardProps {
  item: LibraryTile;
  state: LibraryTileState;
}

export const LibraryTileCard: React.FC<LibraryTileCardProps> = ({ item, state }) => {
  const Icon = item.icon;
  const isCatalogReview = item.title === 'Catalog Review';
  const reviewPath = reviewDestination(item, state);
  const reviewAction = reviewActionLabel(state);
  const urgent = (state.blockedCount ?? 0) + (state.failedCount ?? 0) > 0;
  return (
    <Card className="group relative flex h-full min-h-[190px] flex-col transition-colors hover:border-accent">
      {/* Icon, title and chevron on one line (owner 2026-10-17). */}
      <div className="flex items-center gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-accent-text">
          <Icon size={20} aria-hidden="true" />
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <h3 className="truncate font-semibold text-heading">
            <Link to={item.path} className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none">
              {item.title}
            </Link>
          </h3>
          {isCatalogReview && state.badge && <Badge variant={state.badge.variant}>{state.badge.label}</Badge>}
        </div>
        <ChevronRight
          size={16}
          className="shrink-0 text-faint transition-colors group-hover:text-accent-text"
          aria-hidden="true"
        />
      </div>
      <div className="mt-3">
        <p className="text-sm text-muted">{item.description}</p>
      </div>
      <div className="mt-auto space-y-2 pt-4">
        <div className="text-xs text-faint">{item.meta}</div>
        {state.stats.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {state.stats.map((stat) => (
              <Chip key={stat}>{stat}</Chip>
            ))}
          </div>
        )}
        {state.nextStep && (
          <div
            className={`flex items-center gap-1 text-xs ${state.nextStepTone === 'danger' ? 'text-danger' : 'text-accent-text'}`}
          >
            {state.nextStepTone === 'danger' && <AlertTriangle size={13} aria-hidden="true" />}
            {state.nextStep}
          </div>
        )}
        {reviewPath && reviewAction && !isCatalogReview && (
          <div className="flex justify-end">
            <Link
              to={reviewPath}
              className={`${buttonStyles({ variant: urgent ? 'danger' : 'primary', appearance: 'outline', size: 'xs' })} relative z-10`}
            >
              {reviewAction}
              <ChevronRight size={13} aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>
    </Card>
  );
};
